// src/pages/purchase/PurchaseBillForm.jsx
// D-291 P3d-5a - PURCHASE BILL: the vendor's bill for ONE goods receipt.
//
// Staff type only what is printed on the vendor's bill. Everything else is
// pre-filled (initialBillLines) and every total comes from planBillFor() -
// the SAME function purchaseBillService.createPurchaseBill runs inside its
// transaction, so the total on this screen is the total that is saved.
// CGST / SGST / IGST, round off and total are never typed. Saving = ONE
// transaction in the service: bill + vendor payable (khaata) + receipt
// "billed" (+ invoice-number key for a tax invoice; + Vendor Master GSTIN
// when it was missing / invalid and the bill shows a valid one). Stock is
// not changed.
//
// P3d-5a-6: the vendor GSTIN PRINTED ON THE BILL decides CGST + SGST / IGST -
// there is no "which GST?" question. No GSTIN on the bill -> GST 0.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { canEnterBills, createPurchaseBill, todayIST } from "../../services/purchaseBillService";
import { initialBillLines, planBillFor } from "../../utils/billPlan";
import { normaliseGstin, validateGstin, vendorGstinOf, vendorGstStatus } from "../../utils/gstBill";

const inr = (n) => (n === null || n === undefined || Number.isNaN(Number(n))
  ? "-"
  : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const toDate = (ts) => (ts?.toDate ? ts.toDate() : ts?.seconds ? new Date(ts.seconds * 1000) : null);
const numOrUndef = (v) => (String(v ?? "").trim() === "" ? undefined : Number(v));
const emptyCharge = () => ({ description: "", amount: "", gst_percent: "", hsn_code: "" });

const box = "w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-100";

export default function PurchaseBillForm() {
  const { receiptId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const companyId = currentUser?.company_id || "";
  const allowed = canEnterBills(currentUser);
  const today = todayIST();

  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(null);       // { text, poId }
  const [receipt, setReceipt] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [company, setCompany] = useState(null);

  const [billType, setBillType] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [lines, setLines] = useState([]);
  const [charges, setCharges] = useState([]);
  const [roundOff, setRoundOff] = useState("");
  const [printedTotal, setPrintedTotal] = useState("");
  // P3d-5a-6: the vendor GSTIN printed on the bill (pre-filled from Vendor Master).
  const [gstinOnBill, setGstinOnBill] = useState("");
  const [updateMaster, setUpdateMaster] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ── load receipt + PO + vendor + company (company checked on each) ──
  useEffect(() => {
    let cancelled = false;
    const stop = (text, poId) => { if (!cancelled) setBlocked({ text, poId: poId || null }); };
    const load = async () => {
      setLoading(true);
      setBlocked(null);
      try {
        const rcSnap = await getDoc(doc(db, "purchase_receipts", receiptId));
        if (!rcSnap.exists()) return stop("Receipt not found.");
        const rc = { id: rcSnap.id, ...rcSnap.data() };
        if (rc.company_id !== companyId) return stop("Access denied.");
        if (rc.bill_status === "billed" || rc.purchase_bill_id) {
          return stop("The vendor's bill for this receipt is already entered.", rc.purchase_order_id);
        }
        if (rc.bill_status === "not_needed") {
          return stop("Nothing was received on this receipt - no bill is needed.", rc.purchase_order_id);
        }
        if (rc.bill_status !== "awaiting_bill") {
          return stop("This receipt is from before purchase bills - its vendor bill is already in Payables.", rc.purchase_order_id);
        }

        const poSnap = rc.purchase_order_id ? await getDoc(doc(db, "purchase_orders", rc.purchase_order_id)) : null;
        if (!poSnap || !poSnap.exists()) return stop("Purchase order of this receipt not found.");
        const poData = { id: poSnap.id, ...poSnap.data() };
        if (poData.company_id !== companyId) return stop("Access denied.");
        if (poData.purchase_mode === "direct_site_purchase" && poData.site_confirmation_status !== "completed") {
          return stop("This site purchase did not finish finalizing - its bill cannot be entered yet. Ask admin to check the PO first.", poData.id);
        }

        const vSnap = rc.vendor_id ? await getDoc(doc(db, "vendors", rc.vendor_id)) : null;
        if (!vSnap || !vSnap.exists()) return stop("Vendor of this receipt not found.", poData.id);
        const v = vSnap.data();
        if (v.company_id !== companyId) return stop("Access denied.");

        const cSnap = await getDoc(doc(db, "companies", companyId));
        const c = cSnap.exists() ? cSnap.data() : {};

        if (cancelled) return;
        setReceipt(rc);
        setGstinOnBill(vendorGstStatus(v) === "unregistered" ? "" : vendorGstinOf(v));
        setVendor(v);
        setCompany(c);
        setLines(initialBillLines(rc.items || []).map((l) => ({
          ...l,
          _rateFromBill: l.base_rate === "",
          _gstFromBill:  l.gst_percent === "", // D-325: left blank on the PO
        })));
      } catch (e) {
        console.error("[PurchaseBillForm] load failed:", e);
        stop("Could not load this receipt. Check your connection and refresh the page.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (companyId && allowed) load(); else setLoading(false);
    return () => { cancelled = true; };
  }, [receiptId, companyId, allowed]);

  // No GSTIN on the bill -> the vendor cannot charge GST: every line goes as 0.
  const noGstin = normaliseGstin(gstinOnBill) === "";

  // ── what was typed, in the shape the service takes ──
  const bill = useMemo(() => ({
    bill_type:           billType,
    vendor_invoice_no:   billType === "tax_invoice" ? invoiceNo : "",
    vendor_invoice_date: billType === "tax_invoice" ? invoiceDate : "",
    lines: lines.map((l) => ({
      line_id: l.line_id, billed_qty: l.billed_qty, billed_unit: l.billed_unit,
      base_rate: l.base_rate, gst_percent: noGstin ? "0" : l.gst_percent, hsn_code: l.hsn_code,
    })),
    charges:       charges.map((c) => ({ ...c, gst_percent: noGstin ? "0" : c.gst_percent })),
    round_off:     numOrUndef(roundOff),
    printed_total: numOrUndef(printedTotal),
    vendor_gstin_on_bill: gstinOnBill,
    update_vendor_master: updateMaster,
  }), [billType, invoiceNo, invoiceDate, lines, charges, roundOff, printedTotal, gstinOnBill, updateMaster, noGstin]);

  // The SAME maths the service runs inside its transaction.
  const plan = useMemo(() => (receipt
    ? planBillFor({ receiptItems: receipt.items || [], company, vendor, bill, today })
    : null), [receipt, company, vendor, bill, today]);

  // GST boxes show 0 and are locked while the bill shows no GSTIN.
  const unregistered = noGstin;
  const gstinCheck = noGstin ? null : validateGstin(gstinOnBill);
  const companyGstinOk = validateGstin(company?.gst_number).ok;

  const setLine = (i, field, value) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const setCharge = (i, field, value) =>
    setCharges((prev) => prev.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));

  const handleSave = async () => {
    if (saving || !plan) return;
    setSaveError(null);
    if (!plan.ok) { setShowErrors(true); return; }
    setSaving(true);
    try {
      const r = await createPurchaseBill({ company_id: companyId, receipt_id: receipt.id, bill, user: currentUser });
      const warn = (r.warnings || []).length > 0 ? `\n\nPlease check:\n- ${r.warnings.join("\n- ")}` : "";
      alert(
        `Bill saved ✓\n${receipt.po_number || ""} · ${receipt.vendor_name || ""}\n` +
        `${r.bill_type === "proforma" ? "Proforma (tax invoice pending)" : "Tax invoice"} · ₹${inr(r.bill_total)} added to the vendor's khaata.` +
        (r.vendor_master_updated ? "\nVendor Master GSTIN updated from this bill." : "") + warn
      );
      navigate(`/purchase-bills/${r.bill_id}`, { replace: true });
    } catch (e) {
      console.error("[PurchaseBillForm] save failed:", e);
      setSaveError(e?.message || "Saving failed. Nothing was saved - try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── screens ──
  if (!allowed) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700">
          Only Owner, Accounts, SM, Manager or Store can enter vendor bills.
        </div>
      </div>
    );
  }
  if (loading) return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  if (blocked) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 mb-4">← Back</button>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">
          <p>{blocked.text}</p>
          <div className="flex gap-2 mt-3">
            {blocked.poId && (
              <button onClick={() => navigate(`/purchase-orders/${blocked.poId}`)} className="px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-xs font-semibold">
                Open the PO
              </button>
            )}
            <button onClick={() => navigate("/awaiting-bills")} className="px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-xs font-semibold">
              Awaiting Bills
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!receipt || !plan) return null;

  const received = toDate(receipt.created_at);
  const isSite = receipt.is_direct_site_purchase === true || receipt.receipt_type === "direct_site_purchase";
  const diff = typeof plan.printed_diff === "number" ? plan.printed_diff : null;
  const printedTyped = numOrUndef(printedTotal) !== undefined;
  // D-317 (R12): "matches" only when our bill total IS the printed total -
  // the payable takes our total, so a ₹0.32 gap is a difference, not a match.
  const printedOk = printedTyped && diff === 0;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto pb-24">
      <button onClick={() => navigate(-1)} className="text-sm text-gray-500 mb-3">← Back</button>

      {/* ── header ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <h1 className="text-lg font-bold text-gray-900">Enter vendor's bill</h1>
        <p className="text-sm text-gray-700 mt-0.5">
          <span className="font-semibold">{receipt.po_number || "PO"}</span> · {receipt.vendor_name || "-"}
          {isSite && <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">Direct Site{receipt.site_name ? ` - ${receipt.site_name}` : ""}</span>}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Received {received ? received.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
          {" · "}received value ₹{inr(receipt.grand_total)}
        </p>
        <p className="text-xs text-gray-500 mt-1">Type what is printed on the bill. Taxes and totals are worked out for you.</p>
      </div>

      {/* "Not saved" messages are shown right above the Save button (below). */}

      {/* ── 1. bill type + invoice ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <p className="text-sm font-semibold text-gray-800 mb-2">1. What kind of bill is it?</p>
        <div className="grid grid-cols-2 gap-2">
          {[
            ["tax_invoice", "Tax invoice", "Has an invoice no"],
            ["proforma", "Proforma", "No invoice no yet - tax invoice comes later"],
          ].map(([value, label, hint]) => (
            <button key={value} type="button" onClick={() => setBillType(value)}
              className={`text-left border rounded-xl px-3 py-2.5 ${billType === value ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200" : "border-gray-300 bg-white"}`}>
              <p className="text-sm font-semibold text-gray-900">{label}</p>
              <p className="text-[11px] text-gray-500">{hint}</p>
            </button>
          ))}
        </div>
        {billType === "tax_invoice" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Invoice no (as printed) *</label>
              <input className={box} value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="e.g. INV/24-25/118" maxLength={30} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Invoice date *</label>
              <input type="date" className={box} value={invoiceDate} max={today} onChange={(e) => setInvoiceDate(e.target.value)} />
            </div>
          </div>
        )}
        {billType === "proforma" && (
          <p className="text-xs text-amber-700 mt-2">
            A proforma goes to the vendor's khaata now, but no GST input credit is taken until the tax invoice is entered.
          </p>
        )}
      </div>

      {/* ── P3d-5a-6: the vendor's GSTIN as printed on the bill ── */}
      <div className={`bg-white border rounded-xl p-4 mb-4 ${gstinCheck?.ok && companyGstinOk ? "border-gray-200" : "border-amber-300"}`}>
        <p className="text-sm font-semibold text-gray-800">Vendor GSTIN on this bill</p>
        <p className="text-xs text-gray-500 mb-2">Filled from Vendor Master - check it against the GSTIN printed on the bill.</p>
        <input className={`${box} font-mono uppercase`} value={gstinOnBill} maxLength={20}
          onChange={(e) => setGstinOnBill(e.target.value)} placeholder="15 characters, e.g. 09AAQFB2219L1Z5" />
        {!companyGstinOk && (
          <p className="text-xs text-red-600 mt-2">Your company's GSTIN in Company Profile is missing or not valid - ask the owner to fix it. Bills with GST cannot be entered until then.</p>
        )}
        {noGstin && (
          <p className="text-xs text-amber-700 mt-2">No GSTIN on the bill - a vendor without GSTIN cannot charge GST, so every line is GST 0. If the bill shows GST, type the GSTIN printed on it, or ask the vendor for a correct tax invoice.</p>
        )}
        {gstinCheck && !gstinCheck.ok && (
          <p className="text-xs text-red-600 mt-2">✗ {gstinCheck.reason}</p>
        )}
        {gstinCheck?.ok && companyGstinOk && (
          <p className="text-xs text-green-700 mt-2">
            ✓ Valid · state {gstinCheck.state_code} · {plan.supply_type === "inter" ? "IGST (other state)" : "CGST + SGST (same state)"}
          </p>
        )}
        {plan.vendor_master_fix && (
          <label className="flex items-start gap-2 text-xs text-gray-700 mt-2">
            <input type="checkbox" checked={updateMaster} onChange={(e) => setUpdateMaster(e.target.checked)} className="mt-0.5" />
            <span>Also save this GSTIN in Vendor Master (it is missing or not valid there), so future POs and bills use it.</span>
          </label>
        )}
      </div>

      {/* ── 2. lines ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <p className="text-sm font-semibold text-gray-800 mb-1">2. Items on the bill</p>
        <p className="text-xs text-gray-500 mb-3">Filled from the PO. Change only what is different on the bill.</p>
        <div className="space-y-3">
          {lines.map((l, i) => {
            const out = plan.lines.find((x) => x.line_id === l.line_id);
            return (
              <div key={l.line_id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between gap-2 flex-wrap mb-2">
                  <p className="text-sm font-semibold text-gray-900">{l.item_name}</p>
                  <p className="text-xs text-gray-500">Received {l.received_qty} {l.unit}</p>
                </div>
                {l._rateFromBill && (
                  <p className="text-[11px] text-amber-700 mb-2">Rate was not agreed on the PO - type the rate from the bill.</p>
                )}
                {l._gstFromBill && !unregistered && (
                  <p className="text-[11px] text-amber-700 mb-2">GST % is not on the PO - type it from the bill.</p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                  <div>
                    <label className="text-[11px] text-gray-500 block mb-0.5">Billed qty</label>
                    <input type="number" inputMode="decimal" step="any" className={box} value={l.billed_qty} onChange={(e) => setLine(i, "billed_qty", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-500 block mb-0.5">Unit on bill</label>
                    <input className={box} value={l.billed_unit} onChange={(e) => setLine(i, "billed_unit", e.target.value)} maxLength={12} />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-500 block mb-0.5">Rate before GST ₹</label>
                    <input type="number" inputMode="decimal" step="any" className={`${box} ${l.base_rate === "" ? "border-amber-400" : ""}`} value={l.base_rate} onChange={(e) => setLine(i, "base_rate", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-500 block mb-0.5">GST %</label>
                    <input type="number" inputMode="decimal" step="any" className={`${box} ${!unregistered && l.gst_percent === "" ? "border-amber-400" : ""}`} value={unregistered ? "0" : l.gst_percent} disabled={unregistered} onChange={(e) => setLine(i, "gst_percent", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-500 block mb-0.5">HSN</label>
                    <input inputMode="numeric" className={box} value={l.hsn_code} maxLength={8} placeholder="Optional" onChange={(e) => setLine(i, "hsn_code", e.target.value.replace(/\s+/g, ""))} />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-500 block mb-0.5">Taxable</label>
                    <p className="px-1 py-2 text-sm font-semibold text-gray-900">{out?.taxable != null ? `₹${inr(out.taxable)}` : "-"}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 3. charges ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <p className="text-sm font-semibold text-gray-800 mb-1">3. Other charges on the bill (optional)</p>
        <p className="text-xs text-gray-500 mb-3">Freight, packing, loading - only if the vendor billed them. They are added to the bill, not to stock value.</p>
        {charges.map((c, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2 items-end">
            <div className="col-span-2">
              <label className="text-[11px] text-gray-500 block mb-0.5">What for</label>
              <input className={box} value={c.description} onChange={(e) => setCharge(i, "description", e.target.value)} placeholder="e.g. Freight" />
            </div>
            <div>
              <label className="text-[11px] text-gray-500 block mb-0.5">Amount before GST ₹</label>
              <input type="number" inputMode="decimal" step="any" className={box} value={c.amount} onChange={(e) => setCharge(i, "amount", e.target.value)} />
            </div>
            <div>
              <label className="text-[11px] text-gray-500 block mb-0.5">GST %</label>
              <input type="number" inputMode="decimal" step="any" className={box} value={unregistered ? "0" : c.gst_percent} disabled={unregistered} onChange={(e) => setCharge(i, "gst_percent", e.target.value)} />
            </div>
            <button type="button" onClick={() => setCharges((prev) => prev.filter((_, idx) => idx !== i))} className="text-xs text-red-600 py-2">Remove</button>
          </div>
        ))}
        <button type="button" onClick={() => setCharges((prev) => [...prev, emptyCharge()])} className="text-sm text-indigo-600 font-medium">+ Add a charge</button>
      </div>

      {/* ── 4. totals ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 text-sm">
        <p className="text-sm font-semibold text-gray-800 mb-2">4. Check the total</p>
        <div className="space-y-1">
          <div className="flex justify-between"><span className="text-gray-600">Taxable</span><span>₹{inr(plan.taxable_total)}</span></div>
          {plan.supply_type === "intra" && (
            <>
              <div className="flex justify-between"><span className="text-gray-600">CGST</span><span>₹{inr(plan.cgst)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">SGST</span><span>₹{inr(plan.sgst)}</span></div>
            </>
          )}
          {plan.supply_type === "inter" && (
            <div className="flex justify-between"><span className="text-gray-600">IGST</span><span>₹{inr(plan.igst)}</span></div>
          )}
          {plan.supply_type === "unregistered" && (
            <div className="flex justify-between"><span className="text-gray-600">GST</span><span className="text-gray-500">none (no GSTIN on the bill)</span></div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-gray-600">
              Round off
              {plan.round_off_source === "printed" && (
                <span className="block text-xs text-green-700">set from the printed total</span>
              )}
            </span>
            <input type="number" inputMode="decimal" step="0.01" className="w-28 px-2 py-1 border border-gray-300 rounded-lg text-sm text-right"
              value={roundOff} onChange={(e) => setRoundOff(e.target.value)} placeholder={plan.round_off != null ? String(plan.round_off) : "auto"} />
          </div>
          <div className="flex justify-between border-t border-gray-200 pt-2 mt-1">
            <span className="font-bold text-gray-900">Bill total</span>
            <span className="font-bold text-gray-900 text-base">
              {plan.bill_total != null ? `₹${inr(plan.bill_total)}` : <span className="text-xs font-normal text-amber-700">fix the GSTIN above to see the total</span>}
            </span>
          </div>
          <div className="flex justify-between items-center pt-1">
            <span className="text-gray-600">Total as printed on the bill</span>
            <div className="flex items-center gap-2">
              <input type="number" inputMode="decimal" step="0.01" className="w-32 px-2 py-1 border border-gray-300 rounded-lg text-sm text-right"
                value={printedTotal} onChange={(e) => setPrintedTotal(e.target.value)} placeholder="Type it" />
              {printedTyped && plan.bill_total != null && (
                printedOk
                  ? <span className="text-green-700 font-semibold">✓ matches</span>
                  : <span className="text-red-600 font-semibold">✗ differs{diff !== null ? ` by ₹${inr(Math.abs(diff))}` : ""}</span>
              )}
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          {!billType
            ? "Choose the bill type above to see the GST input credit."
            : billType === "tax_invoice" && !plan.gst_in_stock_cost && plan.tax_total != null
            ? `GST input credit on this bill: ₹${inr(plan.tax_total)} (once the bill is saved).`
            : billType === "proforma"
            ? "No GST input credit yet - a proforma gets it when the tax invoice is entered."
            : "No GST input credit on this bill (no GSTIN on the bill, or your company does not claim it)."}
          {" "}Round off is taken from the printed total when it is within ₹1.00; otherwise to the nearest rupee - change it only if the bill shows another figure.
        </p>
      </div>

      {plan.warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-sm text-amber-900">
          <p className="font-semibold mb-1">Please check:</p>
          <ul className="list-disc ml-5 space-y-0.5">{plan.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}


      {/* Errors right above the Save button - where the eyes are when Save is pressed. */}
      {showErrors && plan.errors.length > 0 && (
        <div role="alert" className="bg-red-50 border border-red-300 rounded-xl p-4 mb-3 text-sm text-red-700">
          <p className="font-semibold mb-1">Not saved - please fix:</p>
          <ul className="list-disc ml-5 space-y-0.5">{plan.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}
      {saveError && (
        <div role="alert" className="bg-red-50 border border-red-300 rounded-xl p-4 mb-3 text-sm text-red-700 whitespace-pre-line">
          <p className="font-semibold mb-1">Not saved</p>
          {saveError}
        </div>
      )}
      <button type="button" onClick={handleSave} disabled={saving}
        className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm disabled:opacity-50">
        {saving ? "Saving..." : `Save bill${plan.bill_total != null ? ` - ₹${inr(plan.bill_total)}` : ""}`}
      </button>
      <p className="text-xs text-gray-500 text-center mt-2">Saving adds this bill to the vendor's khaata. Stock is not changed.</p>
    </div>
  );
}